import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import {
  DownloadIcon,
  FileArchiveIcon,
  FileAudioIcon,
  FileIcon,
  FileImageIcon,
  FileTextIcon,
  FileVideoIcon,
  PresentationIcon,
  SheetIcon,
} from "lucide-react";

import { formatDateAt } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import { DownloadLink } from "simplydo/react/components/DownloadLink";
import { styled } from "simplydo/react/styled";
import { Icon, IconData } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import { Message } from "simplydo/react/ui/message";
import { Pagination } from "simplydo/react/ui/pagination";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useAppSelector } from "store";

import { EmptyBox } from "../UI";
import { StyledPaginationContainer } from "./Notifications";

const DownloadsContainer = styled.div`
  overflow: auto;
  padding-right: 0.5rem;
`;

export const DownloadItemContainer = styled.div<{ $disabled?: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  text-overflow: ellipsis;
  margin: 0.5rem 0;
  border-radius: 0.5rem;
  color: black;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  width: 100%;

  position: relative;

  &:hover {
    background-color: ${({ $disabled, theme }) =>
      $disabled ? "transparent" : theme.colors.primary.softer};
  }

  &:first-child {
    margin-top: 0;
  }

  &:after {
    content: "";
    position: absolute;
    bottom: -0.25rem;
    left: 1rem;
    right: 1rem;
    width: calc(100% - 2rem);
    height: 1px;
    background-color: ${({ theme }) => theme.colors.primary.softer};
    border-radius: 0.5rem;
  }
`;

type DownloadsProps = {
  downloadCompleted: boolean;
  page: number;
  setPage: (page: number) => void;
};

export const FileIconMap: Record<string, IconData> = {
  pdf: FileTextIcon,
  doc: FileTextIcon,
  docx: FileTextIcon,
  xls: SheetIcon,
  xlsx: SheetIcon,
  ppt: PresentationIcon,
  pptx: PresentationIcon,
  jpg: FileImageIcon,
  jpeg: FileImageIcon,
  png: FileImageIcon,
  gif: FileImageIcon,
  mp4: FileVideoIcon,
  mp3: FileAudioIcon,
  zip: FileArchiveIcon,
  rar: FileArchiveIcon,
  txt: FileTextIcon,
  other: FileIcon,
  csv: FileIcon,
};

type FileIconKey = keyof typeof FileIconMap;

const getFileIcon = (ext: string | undefined): IconData => {
  if (ext && ext in FileIconMap) {
    return FileIconMap[ext as FileIconKey] ?? FileIcon;
  }
  return FileIcon;
};

const downloadTypeMap = {
  ideaPdfExport: "$t(generic.idea,capitalise) PDF document",
  ideaDocxExport: "$t(generic.idea,capitalise) Word document",
  challengePdfExport: "Engagement report",
  assessmentXlsxExport: "Assessment report",
  userAssessmentXlsxExport: "Personal assessment report",
  pollPdfExport: "Poll report",
  pollCsvExport: "Poll results",
  groupBusinessCsvExport: "Group business list",
  groupUserCsvExport: "Group user list",
  challengeXlsxExport:
    "$t(generic.challenge,capitalise) $t(generic.idea) report",
} as const;

type DownloadTypeKey = keyof typeof downloadTypeMap;

const getDownloadTypeLabel = (downloadType: string): string => {
  if (downloadType in downloadTypeMap) {
    return downloadTypeMap[downloadType as DownloadTypeKey];
  }
  return downloadType;
};

const Downloads = ({ downloadCompleted, page, setPage }: DownloadsProps) => {
  const user = useAppSelector((state) => state.user);
  const userId = user?._id;
  const { t } = useTranslation();

  const downloadsQuery = useQuery(
    initUseQuery(api.users.getDownloads, [userId ?? "", { limit: 10, page }], {
      isEnabled: !!userId,
      refetchInterval: ({ state }) => {
        if (state.data?.downloads?.some((d) => d.status === "pending")) {
          return 5000;
        }
        return false;
      },
    }),
  );

  const { downloads = [] } = downloadsQuery?.data ?? {};
  const hasPending = downloads.some((d) => d.status === "pending");

  if (downloads.length === 0) {
    return <EmptyBox message="You have not created any downloads" />;
  }
  return (
    <>
      <DownloadsContainer>
        {!downloadCompleted && hasPending && (
          <Message color="blue">
            Your download has started, we will notify you once it is completed.
          </Message>
        )}
        {downloadCompleted && (
          <Message color="green">
            Your download has finished, you can download it by clicking below.
          </Message>
        )}
        {downloads.map((download) => {
          const displayName = download.context.displayName;
          const ext = displayName.split(".").pop();
          const icon = getFileIcon(ext);
          const isPending = download.status === "pending";
          const isSuccess = download.status === "success";

          if (isPending) {
            return (
              <DownloadItemContainer key={download._id} $disabled>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Icon icon={icon} size="large" style={{ marginRight: 12 }} />
                  <div style={{ maxWidth: 420 }}>
                    <p
                      style={{
                        marginBottom: 2,
                        maxWidth: "100%",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {displayName}
                    </p>
                    <p
                      style={{
                        color: "grey",
                        fontSize: 12,
                        lineHeight: "10px",
                        marginBottom: 0,
                      }}
                    >
                      {t(getDownloadTypeLabel(download.context.downloadType))}
                    </p>
                    <span
                      style={{
                        color: "grey",
                        fontSize: 12,
                        lineHeight: "10px",
                      }}
                    >
                      {formatDateAt(download.createdAt)}
                    </span>
                  </div>
                </div>
                <Loader size="small" style={{ margin: 0 }} />
              </DownloadItemContainer>
            );
          }

          return (
            <Popup
              key={download._id}
              disabled={isSuccess}
              trigger={
                <DownloadLink
                  className="item"
                  url={download.url ? download.url : ""}
                  name={download.context.displayName}
                  style={{
                    display: "flex",
                    textOverflow: "ellipsis",
                    cursor:
                      download.status !== "failed" &&
                      download.status !== "expired"
                        ? "pointer"
                        : undefined,
                    opacity:
                      download.status === "failed" ||
                      download.status === "expired"
                        ? 0.6
                        : 1,
                  }}
                >
                  <DownloadItemContainer
                    $disabled={!isSuccess}
                    onClick={(e) => {
                      if (!isSuccess) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Icon
                        icon={icon}
                        size="large"
                        style={{ marginRight: 12 }}
                      />
                      <div style={{ maxWidth: 420 }}>
                        <p
                          style={{
                            marginBottom: 2,
                            maxWidth: "100%",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {download.context.displayName}
                        </p>
                        <p
                          style={{
                            color: "grey",
                            fontSize: 12,
                            lineHeight: "10px",
                            marginBottom: 0,
                          }}
                        >
                          {t(
                            getDownloadTypeLabel(download.context.downloadType),
                          )}
                        </p>
                        <span
                          style={{
                            color: "grey",
                            fontSize: 12,
                            lineHeight: "10px",
                          }}
                        >
                          {formatDateAt(download.createdAt)}
                          {download.status === "failed" ? (
                            <span style={{ color: "#e7381c", fontSize: 12 }}>
                              {" "}
                              - Download failed
                            </span>
                          ) : null}
                          {download.status === "expired" ? (
                            <span style={{ fontSize: 12, marginTop: -2 }}>
                              {" "}
                              - Download expired
                            </span>
                          ) : null}
                        </span>
                      </div>
                    </div>
                    {isSuccess ? <Icon icon={DownloadIcon} /> : null}
                  </DownloadItemContainer>
                </DownloadLink>
              }
            >
              {
                <div style={{ maxWidth: 300 }}>
                  {download.status === "failed"
                    ? "Something went wrong when trying to generate this file. Please try again, or contact us directly if this issue persists."
                    : "This download link has expired, please generate the download again if needed."}
                </div>
              }
            </Popup>
          );
        })}
      </DownloadsContainer>
      {downloadsQuery?.data?.total && downloadsQuery.data.total > 30 ? (
        <StyledPaginationContainer>
          <Pagination
            totalPages={Math.ceil((downloadsQuery.data?.total ?? 0) / 30)}
            activePage={page}
            ellipsisItem={null}
            firstItem={null}
            lastItem={null}
            onPageChange={(_e, { activePage }) => setPage(activePage as number)}
          />
        </StyledPaginationContainer>
      ) : null}
    </>
  );
};

export default Downloads;
